// Platform-admin hooks over the settings/feature-flag registry
// (GET/PATCH /api/v1/admin/settings*, spec 10-11), plus the per-code
// org-override drill-down (GET /admin/settings/{code}/organizations, spec
// 2026-07-04-12-super-admin-settings-registry-ui). Distinct from
// use-feature-flags.ts, which is the org-scoped (non-admin) surface — this
// module is the cross-organization, platform-wide registry view.
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import type { components } from '../api/schema'

export type AdminSettingListEntry = components['schemas']['AdminSettingListEntry']
export type AdminSettingPatchRequest = components['schemas']['AdminSettingPatchRequest']
export type AdminSettingOrganizationOverrideEntry = components['schemas']['AdminSettingOrganizationOverrideEntry']

export type RegistryKindFilter = 'all' | 'flag' | 'setting'

/**
 * useAdminSettingsRegistry loads the full server-wide registry (both kinds by
 * default). The frontend applies the free-text code/name filter client-side;
 * the kind tabs are passed through as the `kind` query param since the
 * backend already discriminates on it cheaply (stage IS NULL/NOT NULL).
 */
export function useAdminSettingsRegistry(kind: RegistryKindFilter) {
    return $api.useQuery('get', '/api/v1/admin/settings', {
        params: { query: { kind } },
    })
}

/**
 * useAdminSettingPatch applies a partial update to one code's registry row
 * (stage/server_value for flag-kind, visibility/default_value/jsonschema for
 * setting-kind). Invalidates the registry list on success so the table
 * reflects the change immediately.
 */
export function useAdminSettingPatch() {
    const qc = useQueryClient()
    return $api.useMutation('patch', '/api/v1/admin/settings/{code}', {
        onSuccess: () => {
            qc.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]
                    return key[0] === 'get' && key[1] === '/api/v1/admin/settings'
                },
            })
        },
    })
}

/**
 * useAdminSettingOrganizations backs the read-only drill-down: organizations
 * holding a stored value for `code`, sorted updated_at:desc. Disabled until a
 * code is provided (the drill-down Sheet mounts its query lazily on open).
 */
export function useAdminSettingOrganizations(code: string | null, limit = 50, offset = 0) {
    return $api.useQuery(
        'get',
        '/api/v1/admin/settings/{code}/organizations',
        {
            params: {
                path: { code: code ?? '' },
                query: { limit, offset },
            },
        },
        { enabled: !!code }
    )
}
